//
// All Android JS Functions here...
//
import { z } from "zod";
import { getCurrentOS } from "./utils";

const DEFAULT_NATIVE_TIMEOUT_MS = 15_000;
const OAUTH_TIMEOUT_MS = 300_000; // 5 min for user-interactive flows

// Zod schemas for Android bridge JSON responses
const AndroidFirebaseTokenSchema = z.object({
  token: z.string().nullish(),
});

const AndroidAppUrlSchema = z.object({
  url: z.string(),
});

const AndroidInstallReferrerSchema = z.object({
  code: z.number(),
  data: z.string().nullable().optional().default(null),
  success: z.boolean().optional().default(false),
  timestamp: z.number().optional().default(0),
  version: z.string().nullable().optional().default(null),
});

const AndroidNotificationStateSchema = z.object({
  success: z.boolean(),
  status: z.enum(["notDetermined", "authorized", "denied"]),
});

// Zod schemas for Android callback-based responses
const AndroidPushAuthorizationResultSchema = z.boolean();

const AndroidSignInSuccessSchema = z.object({
  authProvider: z.string(),
  id: z.string(),
  idToken: z.string(),
  email: z.string(),
  photoUrl: z.unknown(),
  displayName: z.string(),
  familyName: z.string(),
  givenName: z.string(),
  serverAuthCode: z.string(),
  success: z.literal(true),
});

const AndroidSignInErrorSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

// Result can be undefined/null if sign out is called during sign-in, see native Android implementation
const AndroidSignInResultSchema = z.union([
  AndroidSignInSuccessSchema,
  AndroidSignInErrorSchema,
  z.undefined(),
  z.null(),
]);

// Android bridge service interface (mirrors global.d.ts Window.services)
interface AndroidBridgeServices {
  getVersion?: () => string | null;
  getNotificationState: () => string | null;
  requestPushAuthorization: (callbackName: string) => void;
  getFirebaseToken: () => string;
  getFirebaseTokenAsync?: (callbackName: string) => void;
  clearData?: () => void;
  offline?: () => void;
  share: (content: string) => void;
  open: (link: string) => void;
  setClipboard?: (text: string) => void;
  startSignInFlow: (provider: string, callbackName: string) => void;
  signOut: (callbackName: string) => void;
  dismissKeyboard: () => void;
  requestReview: () => void;
  setAppUrl: (url: string) => void;
  getAppUrl: () => string | null;
  resetAppUrl: () => void;
  getInstallReferrer: () => string | null;
  getInstallReferrerAsync?: (callbackName: string) => void;
  scanQrCode: (callbackName: string) => void;
  openPushSettings?: () => void;
  // Optional: shells built before the partner-auth sheet do not expose it.
  // See tools/native-auth-session.ts for the request and reply shapes.
  openAuthSession?: (requestJson: string, callbackName: string) => void;
  saveImage: (
    base64Image: string,
    filename: string,
    callbackName: string
  ) => void;
}

/**
 * Checks if running in the Android app (WebView with bridge available).
 * Evaluates at call time to avoid race conditions where the Android bridge
 * (window.services) isn't injected yet when JS first runs.
 * Result is memoized once the bridge is confirmed ready.
 */
let _isAndroidAppCache: boolean | null = null;
const _isAndroidOS = getCurrentOS() === "android";

export function isAndroidApp(): boolean {
  if (!_isAndroidOS) return false;
  if (_isAndroidAppCache === true) return true;
  const ready = window.services !== undefined;
  if (ready) _isAndroidAppCache = true;
  return ready;
}

function compactLog(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 1200);
}

function safeStringify(value: unknown): string {
  try {
    const seen = new WeakSet<object>();
    const json = JSON.stringify(value, (_key, current) => {
      if (current instanceof Error) {
        return {
          name: current.name,
          message: current.message,
          stack: current.stack,
        };
      }
      if (typeof current === "object" && current !== null) {
        if (seen.has(current)) return "[Circular]";
        seen.add(current);
      }
      return current;
    });
    return compactLog(json ?? String(value));
  } catch {
    return compactLog(String(value));
  }
}

function errorToLogMessage(error: unknown): string {
  if (error instanceof Error) {
    return compactLog(`${error.name}: ${error.message}`);
  }
  return safeStringify(error);
}

function summarizeZodIssues(issues: z.ZodIssue[]): string {
  if (issues.length === 0) return "none";
  return compactLog(
    issues
      .map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join(".") : "<root>";
        return `${path}:${issue.code}:${issue.message}`;
      })
      .join(" | ")
  );
}

type FlatLogPrimitive = string | number | boolean | null;

function toFlatLogPayload(
  payload: Record<string, unknown>
): Record<string, FlatLogPrimitive> {
  const flatPayload: Record<string, FlatLogPrimitive> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined) {
      continue;
    }
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      flatPayload[key] = value;
      continue;
    }
    flatPayload[key] = safeStringify(value);
  }
  return flatPayload;
}

function logAndroidDeepLinkFailure(
  stage: string,
  payload: Record<string, unknown>
): void {
  console.error(
    `DDL_ANDROID_NATIVE_FAILURE ${safeStringify(toFlatLogPayload({ stage, ...payload }))}`
  );
}

/**
 * Helper function to create a Promise-based callback for Android native methods.
 * Generates a unique callback name and sets up the Promise resolution.
 *
 * @param serviceMethod - The Android service method to call (e.g., 'requestPushAuthorization')
 * @param schema - Zod schema to validate and type the response
 * @param serviceArgs - Additional arguments to pass to the service method (before callback)
 * @param timeoutMs - Timeout in milliseconds (default 15s, use OAUTH_TIMEOUT_MS for sign-in)
 * @returns A Promise that resolves when the native callback is invoked
 */
function createAndroidCallbackPromise<T extends z.ZodTypeAny>(
  serviceMethod: keyof AndroidBridgeServices,
  schema: T,
  serviceArgs: unknown[] = [],
  timeoutMs = DEFAULT_NATIVE_TIMEOUT_MS
): Promise<z.infer<T>> {
  if (!isAndroidApp()) {
    return Promise.reject(new Error("Not running on native Android"));
  }

  return new Promise<z.infer<T>>((resolve, reject) => {
    const callbackName =
      `${serviceMethod}Callback_${Math.random().toString(36).slice(2, 9)}` as const;

    const timeout = window.setTimeout(() => {
      delete window[callbackName];
      reject(new Error(`Android callback timeout: ${serviceMethod}`));
    }, timeoutMs);

    window[callbackName] = (result: unknown): void => {
      window.clearTimeout(timeout);
      delete window[callbackName];
      const parsed = schema.safeParse(result);
      if (parsed.success) {
        resolve(parsed.data);
      } else {
        const issuesSummary = summarizeZodIssues(parsed.error.issues);
        console.error(
          `Android callback validation failed method=${serviceMethod} resultType=${typeof result} result=${safeStringify(result)} issues=${issuesSummary}`
        );
        reject(
          new Error(`Android callback validation failed: ${serviceMethod}`)
        );
      }
    };

    try {
      const services = window.services;
      if (services === undefined) {
        window.clearTimeout(timeout);
        delete window[callbackName];
        reject(new Error(`Android service method not found: ${serviceMethod}`));
        return;
      }
      const method = services[serviceMethod];
      if (typeof method !== "function") {
        window.clearTimeout(timeout);
        delete window[callbackName];
        reject(new Error(`Android service method not found: ${serviceMethod}`));
        return;
      }
      // Call method directly on services object to preserve Java binding
      // Extracting the method (const fn = services[method]) breaks WebView's JS-to-Java bridge
      (method as (...args: unknown[]) => void).call(
        services,
        ...serviceArgs,
        callbackName
      );
    } catch (error) {
      window.clearTimeout(timeout);
      delete window[callbackName];
      console.error(
        `Android callback invocation failed method=${serviceMethod} error="${errorToLogMessage(error)}"`
      );
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

export function androidGetVersion(): string | null {
  if (!isAndroidApp()) return null;
  return window.services?.getVersion?.() ?? null;
}

export function androidGetNotificationState(): {
  success: boolean;
  status: "notDetermined" | "authorized" | "denied";
} | null {
  if (!isAndroidApp()) {
    return null;
  }
  try {
    const result = window.services?.getNotificationState();
    if (result === undefined || result === null) {
      return null;
    }
    const parsed = AndroidNotificationStateSchema.safeParse(JSON.parse(result));
    return parsed.success ? parsed.data : null;
  } catch (error) {
    console.error(
      `Failed to get notification state: ${errorToLogMessage(error)}`
    );
    return null;
  }
}

export async function androidRequestPushAuthorization(): Promise<boolean> {
  try {
    const notificationState = androidGetNotificationState();
    if (!notificationState) {
      return false;
    }
    const { status } = notificationState;
    if (status === "notDetermined") {
      return createAndroidCallbackPromise(
        "requestPushAuthorization",
        AndroidPushAuthorizationResultSchema
      );
    }
    // If already determined, return the current status
    return status === "authorized";
  } catch (error) {
    console.error(
      `Couldn't request authorization: ${errorToLogMessage(error)}`
    );
    return false;
  }
}

export const androidGetFirebaseToken = (): string | null => {
  if (!isAndroidApp()) {
    return null;
  }
  try {
    const token = window.services?.getFirebaseToken();
    if (token === undefined) return null;
    const parsed = AndroidFirebaseTokenSchema.safeParse(JSON.parse(token));
    return parsed.success ? (parsed.data.token ?? null) : null;
  } catch {
    console.error("Failed to parse Firebase token from Android");
    return null;
  }
};

export async function androidGetFirebaseTokenAsync(): Promise<string | null> {
  if (!isAndroidApp() || window.services?.getFirebaseTokenAsync === undefined) {
    return null;
  }

  try {
    const result = await createAndroidCallbackPromise(
      "getFirebaseTokenAsync",
      AndroidFirebaseTokenSchema
    );
    return result.token ?? null;
  } catch (error) {
    console.error(
      `Failed to get Firebase token asynchronously: ${errorToLogMessage(error)}`
    );
    return null;
  }
}

export const androidClearData = () => {
  if (isAndroidApp() && window.services?.clearData !== undefined) {
    window.services.clearData();
  }
};

export const androidOffline = () => {
  if (isAndroidApp() && window.services?.offline !== undefined) {
    window.services.offline();
  }
};

// `text` null shares the link alone.
export const androidShare = (link: string, text: string | null) => {
  if (isAndroidApp())
    window.services?.share(text === null ? link : text + "\n" + link);
};

export const androidSetClipboard = (text: string): void => {
  if (!isAndroidApp()) return;
  const services = window.services;
  if (services === undefined) return;
  if (typeof services.setClipboard !== "function") return;
  services.setClipboard(text);
};

export const androidOpen = (link: string) => {
  const android = isAndroidApp();
  if (!android) {
    logAndroidDeepLinkFailure("android_open", {
      operation: "open",
      reason: "not_android_app",
      link,
      isAndroidApp: android,
    });
    return;
  }

  const services = window.services;
  if (services === undefined || typeof services.open !== "function") {
    logAndroidDeepLinkFailure("android_open", {
      operation: "open",
      reason: "native_method_missing",
      link,
      servicesDefined: services !== undefined,
      openMethodType: typeof services?.open,
    });
    return;
  }

  try {
    services.open(link);
  } catch (error) {
    logAndroidDeepLinkFailure("android_open", {
      operation: "open",
      reason: "native_invocation_exception",
      link,
      errorName: error instanceof Error ? error.name : null,
      errorMessage:
        error instanceof Error ? error.message : errorToLogMessage(error),
      errorStack: error instanceof Error ? error.stack : null,
    });
  }
};

type AndroidSignInResult = z.infer<typeof AndroidSignInResultSchema>;

export const androidSignIn = async (): Promise<AndroidSignInResult> => {
  return createAndroidCallbackPromise(
    "startSignInFlow",
    AndroidSignInResultSchema,
    ["google"],
    OAUTH_TIMEOUT_MS
  );
};

export const androidSignOut = async (): Promise<void> => {
  if (!isAndroidApp()) {
    return;
  }
  // Note: The native signOut() method should accept a callback parameter
  // to work with the promise-based callback pattern. Currently it uses
  // the stored authCallback from startSignInFlow, but for proper separation
  // it should accept its own callback parameter.
  // The callback is called without arguments when sign out completes
  return createAndroidCallbackPromise("signOut", z.void());
};

export const androidDismissKeyboard = async () => {
  if (isAndroidApp()) window.services?.dismissKeyboard();
};

export const androidRequestReview = async () => {
  if (isAndroidApp()) window.services?.requestReview();
};

export const androidSetAppUrl = async (url: string) => {
  if (isAndroidApp()) window.services?.setAppUrl(url);
};

export const androidGetAppUrl = (): string | null => {
  if (!isAndroidApp()) {
    return null;
  }
  try {
    const result = window.services?.getAppUrl();
    if (result === undefined || result === null) {
      return null;
    }
    // Parse the JSON response: {"url": "some_url"}
    const parsed = AndroidAppUrlSchema.safeParse(JSON.parse(result));
    return parsed.success ? parsed.data.url || null : null;
  } catch (error) {
    console.error(`Failed to get app URL: ${errorToLogMessage(error)}`);
    return null;
  }
};

export const androidResetAppUrl = async () => {
  if (isAndroidApp()) window.services?.resetAppUrl();
};

export type AndroidInstallReferrerResponse = z.infer<
  typeof AndroidInstallReferrerSchema
>;

function normalizeInstallReferrerPayload(
  payload: unknown
): AndroidInstallReferrerResponse | null {
  let candidate: unknown = payload;

  // Some bridge versions can return a JSON string.
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }

  // Some bridge wrappers can nest the actual payload under "result".
  if (
    candidate !== null &&
    typeof candidate === "object" &&
    "result" in candidate &&
    !("code" in candidate)
  ) {
    candidate = (candidate as { result: unknown }).result;
  }

  const parsed = AndroidInstallReferrerSchema.safeParse(candidate);
  if (!parsed.success) {
    return null;
  }

  return parsed.data;
}

/**
 * Gets the install referrer data from the Play Store
 * Returns null if not available or if there's an error
 */
export const androidGetInstallReferrer =
  (): AndroidInstallReferrerResponse | null => {
    if (!isAndroidApp()) {
      return null;
    }

    try {
      const services = window.services;
      if (
        services === undefined ||
        typeof services.getInstallReferrer !== "function"
      ) {
        logAndroidDeepLinkFailure("install_referrer_sync", {
          operation: "getInstallReferrer",
          reason: "native_method_missing",
          servicesDefined: services !== undefined,
          methodType: typeof services?.getInstallReferrer,
        });
        return null;
      }

      const response = services.getInstallReferrer();
      if (response === undefined || response === null) {
        logAndroidDeepLinkFailure("install_referrer_sync", {
          operation: "getInstallReferrer",
          reason: "empty_native_response",
          responseType: typeof response,
        });
        return null;
      }

      const parsed = normalizeInstallReferrerPayload(response);
      if (parsed === null) {
        logAndroidDeepLinkFailure("install_referrer_sync", {
          operation: "getInstallReferrer",
          reason: "invalid_payload",
          rawResult: response,
        });
        return null;
      }

      // Check if the service call was successful (code 0)
      if (parsed.code === 0 && parsed.success && parsed.data !== null) {
        return parsed;
      }

      // Transient state while native referrer client is initializing.
      if (parsed.code === -1 && !parsed.success) {
        return null;
      }

      // Install referrer not supported or service unavailable (expected on
      // devices without Google Play Store, emulators, Huawei devices, etc.)
      if ((parsed.code === 1 || parsed.code === 2) && !parsed.success) {
        return null;
      }

      // Success with null data means no deep link in install referrer.
      if (parsed.code === 0 && parsed.success && parsed.data === null) {
        return null;
      }

      logAndroidDeepLinkFailure("install_referrer_sync", {
        operation: "getInstallReferrer",
        reason: "native_service_error",
        code: parsed.code,
        success: parsed.success,
        dataPresent: parsed.data !== null,
      });
      return null;
    } catch (error) {
      logAndroidDeepLinkFailure("install_referrer_sync", {
        operation: "getInstallReferrer",
        reason: "exception",
        errorName: error instanceof Error ? error.name : null,
        errorMessage:
          error instanceof Error ? error.message : errorToLogMessage(error),
        errorStack: error instanceof Error ? error.stack : null,
      });
      return null;
    }
  };

/**
 * Checks if the native app supports the async install referrer method.
 */
export function androidHasAsyncInstallReferrer(): boolean {
  return (
    isAndroidApp() &&
    typeof window.services?.getInstallReferrerAsync === "function"
  );
}

/**
 * Gets the install referrer data asynchronously via native callback.
 * Only available on newer native builds that expose getInstallReferrerAsync.
 * Returns null if the service call fails or data is not available.
 */
export async function androidGetInstallReferrerAsync(): Promise<AndroidInstallReferrerResponse | null> {
  if (!androidHasAsyncInstallReferrer()) {
    logAndroidDeepLinkFailure("install_referrer_async", {
      operation: "getInstallReferrerAsync",
      reason: "async_install_referrer_not_supported",
    });
    return null;
  }

  try {
    const rawResult = await createAndroidCallbackPromise(
      "getInstallReferrerAsync" as keyof AndroidBridgeServices,
      z.unknown()
    );
    const result = normalizeInstallReferrerPayload(rawResult);

    if (result === null) {
      logAndroidDeepLinkFailure("install_referrer_async", {
        operation: "getInstallReferrerAsync",
        reason: "invalid_payload",
        rawResult,
      });
      return null;
    }

    if (result.code === 0 && result.success && result.data !== null) {
      return result;
    }

    // Transient state while native referrer client is initializing.
    if (result.code === -1 && !result.success) {
      return null;
    }

    // Install referrer not supported or service unavailable (expected on
    // devices without Google Play Store, emulators, Huawei devices, etc.)
    if ((result.code === 1 || result.code === 2) && !result.success) {
      return null;
    }

    // Success with null data means no deep link in install referrer.
    if (result.code === 0 && result.success && result.data === null) {
      return null;
    }

    logAndroidDeepLinkFailure("install_referrer_async", {
      operation: "getInstallReferrerAsync",
      reason: "native_service_error",
      code: result.code,
      success: result.success,
      dataPresent: result.data !== null,
    });
    return null;
  } catch (error) {
    logAndroidDeepLinkFailure("install_referrer_async", {
      operation: "getInstallReferrerAsync",
      reason: "exception",
      errorName: error instanceof Error ? error.name : null,
      errorMessage:
        error instanceof Error ? error.message : errorToLogMessage(error),
      errorStack: error instanceof Error ? error.stack : null,
    });
    return null;
  }
}

// --- FCM token refresh callback (pushed from native when Firebase SDK refreshes the token) ---

let onTokenRefresh: ((token: string) => void) | null = null;

export function setAndroidTokenRefreshHandler(
  handler: (token: string) => void
): void {
  onTokenRefresh = handler;
}

function initAndroidTokenRefreshCallback(): void {
  if (!_isAndroidOS) return;
  window.__onNativeFCMTokenRefresh = (token: string): void => {
    if (onTokenRefresh !== null) onTokenRefresh(token);
  };
}

// Register on every page load
initAndroidTokenRefreshCallback();

// --- Foreground push callback (pushed from native when an FCM push arrives while the app is open) ---

let onForegroundPush: ((payload: unknown) => void) | null = null;

export function setAndroidForegroundPushHandler(
  handler: (payload: unknown) => void
): void {
  onForegroundPush = handler;
}

function initAndroidForegroundPushCallback(): void {
  if (!_isAndroidOS) return;
  window.__onNativePushReceived = (payload: unknown): void => {
    if (onForegroundPush !== null) onForegroundPush(payload);
  };
}

// Register on every page load
initAndroidForegroundPushCallback();

export function androidOpenPushSettings(): void {
  if (isAndroidApp()) window.services?.openPushSettings?.();
}

export async function androidSaveImage(
  base64Image: string,
  filename: string
): Promise<boolean> {
  return createAndroidCallbackPromise("saveImage", z.boolean(), [
    base64Image,
    filename,
  ]);
}
